import { Box, Stack, Typography } from '@dtx-company/design-system/src'
import { FC } from 'react'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image/index'
import { PaymentMethod } from '@stripe/stripe-js'
import { getDefaultPaymentMethodAdditionalText } from '@app/common/src/components/PlanPurchaseExperience/components/GetDefaultPaymentMethodAdditionalText'
import { getDefaultPaymentMethodMainText } from '@app/common/src/components/PlanPurchaseExperience/components/GetDefaultPaymentMethodMainText'
import { images } from '@app/code/src/constants/account'
import { styled } from '@mui/system'

export type PaymentMethodPresentationMode = 'default' | 'fullDescription' | 'short' | 'buyPlan'

export const getPaymentMethodImageSrc = (paymentMethod: PaymentMethod | undefined): string => {
  if (!paymentMethod) return images.unknown
  if (paymentMethod?.card?.wallet?.type) {
    return images[paymentMethod?.card?.wallet?.type] || images.unknownCard
  } else if (paymentMethod?.card) {
    return paymentMethod?.card?.brand && images[paymentMethod.card.brand]
      ? images[paymentMethod.card.brand]
      : images.unknownCard
  } else {
    return images[paymentMethod?.type] || images.unknown
  }
}

export const getPaymentMethodImageAlt = (paymentMethod: PaymentMethod | undefined): string => {
  if (!paymentMethod) return 'unknown payment method logo'
  if (paymentMethod?.card?.wallet?.type) {
    return paymentMethod?.card?.wallet?.type || 'unknown payment method logo'
  } else if (paymentMethod?.card) {
    return paymentMethod?.card?.brand && images[paymentMethod.card.brand]
      ? paymentMethod.card.brand
      : 'unknown payment method logo'
  } else {
    return paymentMethod?.type || 'unknown payment method logo'
  }
}

export const PaymentMethodDisplay: FC<{
  paymentMethod: PaymentMethod
  mode: PaymentMethodPresentationMode
}> = ({ paymentMethod, mode }) => {
  const imageSrc = mode !== 'short' && getPaymentMethodImageSrc(paymentMethod)
  const mainText = getDefaultPaymentMethodMainText(paymentMethod, mode)
  const additionalText = getDefaultPaymentMethodAdditionalText(paymentMethod, mode)

  return (
    <Stack spacing={2} justifyContent={'center'}>
      <Stack spacing={4} direction={'row'} alignItems={'center'}>
        {imageSrc && (
          <_ExistingCreditCardImage
            data-testid={'payment-method-image'}
            src={imageSrc}
            width={24}
            height={15}
            alt="payment method logo"
          />
        )}
        <Typography
          component="span"
          variant={mode === 'short' ? 'subtitleSmall' : 'bodySmall'}
          data-testid={'payment-method-text'}
        >
          {mainText}
        </Typography>
      </Stack>
      {additionalText && (
        <Box sx={{ whiteSpace: 'pre-line' }}>
          <Typography
            component="p"
            variant={'bodySmall'}
            color={'onSurface.secondary'}
            data-testid={'payment-method-additional-text'}
          >
            {additionalText}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}

const _ExistingCreditCardImage = styled(Image)`
  flex: 0 0 auto;
`
